import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import Close32 from '@carbon/icons-react/es/close/32';
import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useBoolean } from 'ahooks';
import { Space } from 'antd';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AccountAPI, RequestPlanPayload, RequestPlanType } from 'api/account';
import { Form } from 'components/atoms/form';
import { Button, LinkButton } from 'components/facade/button';
import { InputNumber, Input } from 'components/facade/input';
import { Page } from 'components/molecules/page';
import { toastService } from 'components/molecules/toast';
import {
  ProductiveHeading01,
  ProductiveHeading03,
  Text01,
} from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';

const Icon = styled.h1`
  ${ProductiveHeading01};
`;

const Heading = styled.h3`
  ${ProductiveHeading03};
`;

const Message = styled.p``;

const StyledPage = styled(Page)`
  padding: var(--spacing-12);
`;

const Container = styled.div`
  width: 400px;
`;

const Text = styled.div``;

const SubmitButton = styled(Button)`
  width: 100%;
  ${Text} {
    flex-grow: 1;
    text-align: left;
  }
`;

const SecondaryButton = styled(LinkButton)`
  ${Text01};
  margin-top: var(--layout-01);
`;

const StyledCloseButton = styled(Button)`
  position: absolute;
  top: var(--spacing-10);
  right: var(--spacing-10);
`;

const useRequestPlanAccountMutation = (
  options: Pick<
    UseMutationOptions<unknown, unknown, RequestPlanPayload>,
    'onSuccess' | 'onError'
  >,
) => {
  return useMutation<unknown, unknown, RequestPlanPayload>({
    mutationFn: (payload) => AccountAPI.requestPlan(payload),
    ...options,
  });
};

interface FormValues {
  noOfAgents: number;
}
interface Props {
  isExpired: boolean;
  handleClose: () => void;
}

export const BuyPage: FC<React.PropsWithChildren<Props>> = ({
  isExpired,
  handleClose: closeForm,
}) => {
  const { user } = useUser();
  const requiredMessage = useTranslation('input.required');
  const errorTitle = useTranslation('account.trial.buy.error.title');
  const errorMessage = useTranslation('account.trial.buy.error.message');
  const shouldBeAtLeast1Message = useTranslation(
    'routes.account_assessment.should_be_at_least_1',
  );
  const [canShowSuccessMessage, { setTrue: showSuccessMessage }] =
    useBoolean(false);
  const { mutate, isLoading } = useRequestPlanAccountMutation({
    onSuccess: () => {
      showSuccessMessage();
    },
    onError: () => {
      toastService({
        title: errorTitle,
        message: errorMessage,
      }).error();
    },
  });

  const handleClose = () => {
    window.location.reload();
  };

  return (
    <StyledPage>
      {canShowSuccessMessage || !isExpired ? (
        <StyledCloseButton
          icon={<Close32 />}
          size="large"
          type="text"
          onClick={() => (canShowSuccessMessage ? handleClose() : closeForm())}
        />
      ) : null}

      <Container>
        {canShowSuccessMessage ? (
          <>
            <Icon>🙌</Icon>
            <Heading>
              <FormattedMessage
                id="account.trial.buy.thanks.heading"
                values={{ firstName: user.firstName || user.lastName }}
              />
            </Heading>
            <Space direction="vertical" size={32}>
              <Message>
                <FormattedMessage
                  id="account.trial.buy.thanks.message"
                  values={{
                    emailId: user.emailId,
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              </Message>
              {!isExpired ? (
                <Button type="primary" onClick={handleClose}>
                  <FormattedMessage id="account.trial.buy.thanks.cta" />
                </Button>
              ) : null}
            </Space>
          </>
        ) : (
          <>
            {!isExpired ? <Icon>🎉</Icon> : null}
            <Heading>
              <FormattedMessage
                id={
                  isExpired
                    ? 'account.trial.expired.buy.heading'
                    : 'account.trial.buy.heading'
                }
              />
            </Heading>
            <Form
              layout="vertical"
              onFinish={(values) =>
                mutate({
                  type: RequestPlanType.Buy,
                  metaData: { plan: 'PREMIUM', ...(values as FormValues) },
                })
              }
              initialValues={{
                emailId: user.emailId,
              }}
            >
              <Form.Item
                label={
                  <FormattedMessage id="account.trial.buy.form.your_email" />
                }
                name="emailId"
              >
                <Input disabled type="email" />
              </Form.Item>
              <Form.Item
                label={<FormattedMessage id="account.trial.buy.form.users" />}
                name="noOfAgents"
                rules={[
                  {
                    min: 1,
                    type: 'number',
                    message: shouldBeAtLeast1Message,
                  },
                  {
                    required: true,
                    message: requiredMessage,
                  },
                ]}
              >
                <InputNumber min={1} autoFocus />
              </Form.Item>
              <SubmitButton
                type="primary"
                iconPlacement="right"
                icon={<ArrowRight16 />}
                disabled={isLoading}
                htmlType="submit"
              >
                <Text>
                  <FormattedMessage id="submit" />
                </Text>
              </SubmitButton>
            </Form>
            {isExpired ? (
              <SecondaryButton
                block
                disabled={isLoading}
                onClick={() => mutate({ type: RequestPlanType.TrialExtend })}
              >
                <FormattedMessage
                  id="account.trial.buy.more_time"
                  values={{
                    u: (chunks) => (
                      <u style={{ padding: '0 var(--spacing-02)' }}>{chunks}</u>
                    ),
                  }}
                />
              </SecondaryButton>
            ) : null}
          </>
        )}
      </Container>
    </StyledPage>
  );
};
